import { Ternary } from '@/components';
import { IconFont } from '@/components/IconFont';
import {
  ExtractTagSelect,
  Tag,
} from '@/pages/CustomCenter/ExtractConfig/components/workflow/components/extract-tag-select';
import { cn, createPromise } from '@/utils';
import { useMount, useUpdate, useUpdateEffect } from 'ahooks';
import { Button, Form, Segmented, message } from 'antd';
import { isNil, set } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { ExtractTagSelectItem } from '../../../components/extract-tag-select/select';
import useExtractTags from '../../../components/extract-tag-select/use-extract-tags';
import { useStore } from '../../../store';
import { AreaExtractionCheckbox } from '../../_base/components/area-extraction-checkbox';
import { QUERY_KEYS } from '@/pages/CustomCenter/ExtractConfig/type';

import {
  SemanticRuleOptions,
  SemanticRuleType,
  semanticRuleType,
} from './const';
import { RegForm } from './reg-form-item';
import { SemanticForm } from './semantic-form-item';
import { Field, Meta, Operation } from './type';
import { useTab } from '@/pages/CustomCenter/ExtractConfig/stores/useTab';
import { useSpecificAreaId } from '@/pages/CustomCenter/ExtractConfig/stores/use-specifc-area-id';
import { useQueryClient } from '@umijs/max';
import { Rule } from 'antd/es/form';
// 抽取规则 正则/语义
const RuleFormItem: React.FC<{
  field: Field;
  operations: Operation;
  meta: Meta;
  prefix: (string | number)[];
  regFormRules: Rule[];
}> = ({ field, prefix, regFormRules }) => (
  <div className="w-full">
    <Form.Item shouldUpdate={() => true} noStyle>
      {({ getFieldValue }) => {
        const type = getFieldValue([...prefix, 'type']) as SemanticRuleType;

        return (
          <Form.Item name={[field.name, 'type']} noStyle>
            <Segmented<SemanticRuleType>
              size="small"
              options={SemanticRuleOptions}
              className={cn(type === semanticRuleType.CONDITION && 'mb-4')}
            />
          </Form.Item>
        );
      }}
    </Form.Item>
    <Form.Item shouldUpdate={() => true}>
      {(form) => {
        const { getFieldValue } = form;
        const type = getFieldValue([...prefix, 'type']) as SemanticRuleType;

        return type === semanticRuleType.CONDITION ? (
          <SemanticForm field={field} form={form} prefix={prefix} />
        ) : (
          <RegForm field={field} rules={regFormRules} />
        );
      }}
    </Form.Item>
  </div>
);

export const RuleAndFieldGroupItem: React.FC<{
  field: Field;
  operations: Operation;
  prefix: (string | number)[];
  readonly?: boolean;
  onDelete?: (fileName: number) => void;
  node_id: string;
  activeField: number;
  setActiveField: (key: number) => void;
}> = ({
  field,
  operations: { remove },
  prefix,
  readonly,
  onDelete,
  node_id,
  activeField,
  setActiveField,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const tagCode: string = Form.useWatch([...prefix, 'tag_code']);
  const { findTag, selectOptions } = useExtractTags();
  const form = Form.useFormInstance();
  const emitter = useStore((s) => s.eventEmitter);
  const [_, setTab] = useTab();
  const [currentNodeId, setCurrentNodeId] = useSpecificAreaId();
  const queryClient = useQueryClient();

  const status = Form.useWatch([...prefix, 'specify_regional_status']);

  const tag = useMemo(
    () => findTag(tagCode, selectOptions),
    [tagCode, findTag, selectOptions],
  );

  // const isSpecificArea = form.getFieldValue([...prefix, 'specify_regional_config']) === 1

  useEffect(() => {
    const currentValue = form.getFieldValue([...prefix]);
    if (!isNil(currentValue)) return;
    setIsCollapsed(false);
  }, []);

  return (
    <div
      key={field.key}
      onClick={() => {
        setActiveField(field.key);
      }}
      // className='relative bg-[var(--bg-main-color)] hover:shadow mb-4 p-2 rounded-md transition'>
      className={`relative  border border-[#E5E7EC] 
    ${activeField === field.key ? 'bg-[#f6f9ff]' : 'bg-white'} 
    mb-4 p-2 rounded-md transition`}
    >
      <div className="-top-2 right-2 absolute flex items-center gap-2">
        <IconFont
          onClick={() => setIsCollapsed(!isCollapsed)}
          type={isCollapsed ? 'icon-zhankaikapian' : 'icon-shouqikapian'}
          style={{
            cursor: 'pointer',
            transform: isCollapsed ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s',
          }}
        />
        <IconFont
          className={cn({
            'cursor-not-allowed pointer-events-none': readonly,
          })}
          onClick={() => onDelete?.(field.name)}
          type="icon-liebiaoshibai"
        />
      </div>

      <div>
        <div style={{ display: isCollapsed ? 'none' : 'block' }}>
          <Form.Item label="字段" name={[field.name, 'tag_code']}>
            <ExtractTagSelectItem acceptedTypes={['text', 'table']} />
          </Form.Item>
          <Form.Item
            label=""
            className="mt-[-16px]"
            valuePropName="checked"
            name={[field.name, 'specify_regional_config']}
          >
            <AreaExtractionCheckbox
              onRedirect={async () => {
                if (readonly) return;
                const { promise, resolve } = createPromise();
                emitter?.emit({
                  type: 'SWITCH_TAB',
                  payload: {
                    resolve,
                  },
                });
                await promise;

                queryClient.refetchQueries({
                  queryKey: [QUERY_KEYS.SPECIFIC_AREA],
                });

                setCurrentNodeId(node_id);
                setTab('SPECIFIC_AREA');
              }}
              showRedirect={!readonly}
              status={status}
              onChange={(checked) => {
                if (checked) {
                  form.setFieldValue([...prefix, 'specify_regional_status'], 0);
                } else {
                  // clear
                  form.setFieldValue(
                    [...prefix, 'specify_regional_status'],
                    undefined,
                  );
                }
              }}
            />
          </Form.Item>
          <Form.Item
            hidden
            name={[...prefix, 'specify_regional_status']}
          ></Form.Item>
          <Form.Item label="规则">
            <Form.List name={[field.name, 'tag_rule_configs']}>
              {(subFields, operations, meta) => {
                const isSpecificArea =
                  form.getFieldValue([...prefix, 'specify_regional_config']) ===
                  1;
                console.log('isSpecificArea', isSpecificArea);

                return (
                  <>
                    {subFields.map((subField) => (
                      <div
                        key={subField.key}
                        className="flex justify-between items-start gap-2 w-full"
                      >
                        <Form.Item name={[subField.name]} noStyle>
                          <RuleFormItem
                            prefix={[
                              ...prefix,
                              'tag_rule_configs',
                              subField.name,
                            ]}
                            field={subField}
                            operations={operations}
                            meta={meta}
                            regFormRules={
                              isSpecificArea
                                ? [{ required: false }]
                                : [
                                    {
                                      required: true,
                                      message: '请输入正则表达式',
                                      warningOnly: true,
                                    },
                                  ]
                            }
                          />
                        </Form.Item>
                        <IconFont
                          className={cn({
                            'cursor-not-allowed pointer-events-none': readonly,
                          })}
                          onClick={() => {
                            if (readonly) return;

                            if (!isSpecificArea && subFields.length === 1) {
                              message.warning('至少保留一个字段或规则');
                              return;
                            }
                            operations.remove(subField.name);
                          }}
                          type="icon-liebiaoshibai"
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      disabled={readonly}
                      className=""
                      onClick={() =>
                        operations.add({ type: semanticRuleType.REGEX })
                      }
                      block
                    >
                      + 添加规则
                    </Button>
                  </>
                );
              }}
            </Form.List>
          </Form.Item>
        </div>
        <div
          style={{ display: isCollapsed ? 'flex' : 'none' }}
          className="justify-between items-center gap-2"
        >
          <Ternary
            condition={Boolean(tag)}
            falseElement={<span className="text-slate-400">未选择字段</span>}
          >
            <Ternary
              condition={tag?.type === 'text'}
              falseElement={
                <div className="space-x-1">
                  <span>{tag?.parent_label}</span>
                  <span className="font-bold">{tag?.label}</span>
                </div>
              }
            >
              <span className="font-bold">{tag?.label}</span>
            </Ternary>
          </Ternary>
        </div>
      </div>
    </div>
  );
};
